import { ConfigProvider } from 'antd'
import type { Locale } from 'antd/es/locale'
import enUS from 'antd/locale/en_US'
import roRO from 'antd/locale/ro_RO'
import ruRU from 'antd/locale/ru_RU'
import dayjs from 'dayjs'
import { useLayoutEffect, useMemo, type PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import { LucideThemeProvider } from 'src/components/LucideThemeProvider'
import { resolveLanguageKey } from 'src/utils'

import { applyBrandCssVars } from './brandCssVars'
import { softTagConfig } from './softTagConfig'
import { getTheme } from './theme'
import { ThemeModeProvider, useThemeMode } from './ThemeModeProvider'

const ANTD_LOCALES: Record<string, Locale> = {
  en: enUS,
  ro: roRO,
  ru: ruRU
}

const ConfigProviderBridge = ({ children }: PropsWithChildren) => {
  const { mode } = useThemeMode()
  const { i18n } = useTranslation()
  const key = resolveLanguageKey(i18n.language)
  const antdLocale = ANTD_LOCALES[key]
  const theme = useMemo(() => getTheme(mode), [mode])
  const colorPrimary = theme.token?.colorPrimary

  useLayoutEffect(() => {
    applyBrandCssVars(colorPrimary, mode)
  }, [colorPrimary, mode])

  // Sync dayjs synchronously during render so the very first paint formats
  // dates in the active language — useEffect runs after paint and caused a
  // brief flicker through the previous locale (typically 'en').
  if (dayjs.locale() !== key) {
    dayjs.locale(key)
  }

  return (
    <ConfigProvider
      theme={theme}
      tag={softTagConfig(mode)}
      locale={antdLocale}
      popupMatchSelectWidth={false}
      // A stray click beside a drawer must not close it and wipe the user's
      // unsaved form input — close only via the X / Cancel / Esc.
      drawer={{ mask: { closable: false } }}
      descriptions={{
        styles: {
          label: { alignItems: 'center' }
        }
      }}
    >
      <LucideThemeProvider>{children}</LucideThemeProvider>
    </ConfigProvider>
  )
}

export const ThemeProviderWrapper = ({ children }: PropsWithChildren) => {
  return (
    <ThemeModeProvider>
      <ConfigProviderBridge>{children}</ConfigProviderBridge>
    </ThemeModeProvider>
  )
}
